import React, { useState, useEffect } from "react";
import { useAxiosInstance } from "../services/Interceptor";
import {
  GetAdminPrice,
  GetEventCombinePrice,
  GetEventsByEventId,
} from "../services/EventsService";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setSubscriptionDetails,
  registerAccountStore,
} from "../store/slices/accountSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Nav from "../components/Nav";
import { PostPayment } from "../services/PaymentService";
import { registerAthleteWithEventId } from "../services/AthleteService";
import { toast, ToastContainer } from "react-toastify";
import { useTrackEvent } from "../hooks/useTrackEvent";
import { getCouponPrice } from "../services/PublicServices";

const DEFAULT_DISCOUNT = {
  amount: 0,
  baseAmount: 0,
  percentage: 0,
  discountType: "",
  couponType: "",
};

export default function AthleteMembershipPlans() {
  const [combinePrice, setCombinePrice] = useState(0);
  const [positionDrillPrice, setPositionDrillPrice] = useState(0);
  const [annualPrice, setAnnualPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPositionDrills, setHasPositionDrills] = useState(false);

  // keep combine always selected; other flags start false
  const [selectedOptions, setSelectedOptions] = useState({
    combine: true,
    positionDrill: false,
    subscription: false,
  });

  const [eventNameSelected, setEventNameSelected] = useState();

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState({ ...DEFAULT_DISCOUNT });
  const [couponError, setCouponError] = useState("");

  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const apiInstance = useAxiosInstance();
  const navigate = useNavigate();

  const storedData = JSON.parse(
    localStorage.getItem("registeredAccount") || "{}",
  );
  const accounts = useSelector((state) => state.account.register);
  const user = accounts.length ? accounts[0] : storedData;

  useEffect(() => {
    if (!accounts.length && storedData) {
      dispatch(registerAccountStore(storedData));
    }
  }, [dispatch, accounts.length, storedData]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, user.eventId);
        if (data) {
          setEventNameSelected(`${data.date} - ${data.name} - ${data.city}`);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadEvents();
  }, [apiInstance, user.eventId]);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        setIsLoading(true);
        const combineData = await GetEventCombinePrice(
          apiInstance,
          user.eventId,
        );
        const adminPrices = await GetAdminPrice(apiInstance);

        const athletePricing = adminPrices?.data?.find(
          (o) => o.role === "Athlete",
        );

        if (athletePricing) {
          const combineP = combineData?.data?.combinePrice ?? 0;
          const positionDrillP = combineData?.data?.positionDrillPrice ?? 0;
          const annualP = athletePricing?.pricingOptions?.annualPrice ?? 0;
          setCombinePrice(combineP);
          setHasPositionDrills(combineData?.data?.hasPositionDrills);
          setPositionDrillPrice(positionDrillP);
          setAnnualPrice(annualP);
          trackEvent("Highschool_ViewedMembershipPlans", {
            event_id: user.eventId,
            combine_price: combineP,
            position_drill_price: positionDrillP,
            annual_price: annualP,
            has_position_drills: combineData?.data?.hasPositionDrills,
            portal_type: "highschool",
          });
        }
      } catch (error) {
        console.error("Error fetching pricing:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPrices();
  }, [apiInstance, user.eventId, trackEvent]);

  useEffect(() => {
    // initialize localStorage with only enabled switches considered
    const initial = {
      combine: true,
      positionDrill: false,
      subscription: false,
    };
    localStorage.setItem("subscriptionDetails", JSON.stringify(initial));
  }, []);

  // Re-validate applied coupon when the user changes their plan selection
  useEffect(() => {
    if (discountInfo.amount <= 0 || !discountInfo.couponType) return;

    const { ok } = validateCouponApplicability(discountInfo.couponType);
    if (!ok) {
      setDiscountInfo({ ...DEFAULT_DISCOUNT });
      setCouponError(
        "Selection changed — coupon removed because it no longer applies.",
      );
      trackEvent("Registration3_5_CouponRemovedDueToSelectionChange", {
        coupon_type: discountInfo.couponType,
        portal_type: "highschool",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  const computeSelectedPrice = (opts) =>
    (combinePrice ?? 0) +
    (hasPositionDrills && opts.positionDrill ? (positionDrillPrice ?? 0) : 0) +
    (opts.subscription ? (annualPrice ?? 0) : 0);

  // ── Coupon helpers ─────────────────────────────────────────────────────────

  const couponNotApplicableMessage = (couponType) => {
    switch (couponType) {
      case "Combine":
        return "This coupon applies to Combine only. Please select a Combine Pass to use it.";
      case "PositionDrills":
        return "This coupon applies to Position Drills only. Please select Position Drills to use it.";
      case "CombineAndPositionDrills":
        return "This coupon applies to Combine and/or Position Drills. Please select at least one to use it.";
      case "Subscription":
        return "This coupon applies to Subscription only. Please select a subscription plan to use it.";
      default:
        return "This coupon is not applicable to your current selection.";
    }
  };

  const validateCouponApplicability = (couponType) => {
    const effectiveCombinePrice = combinePrice; // combine is always selected
    const effectivePositionDrillPrice = selectedOptions.positionDrill
      ? positionDrillPrice
      : 0;

    if (couponType === "Combine") {
      if (effectiveCombinePrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Combine"),
        };
      }
      return { ok: true, eligibleAmount: effectiveCombinePrice };
    }

    if (couponType === "PositionDrills") {
      if (!selectedOptions.positionDrill || effectivePositionDrillPrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("PositionDrills"),
        };
      }
      return { ok: true, eligibleAmount: effectivePositionDrillPrice };
    }

    if (couponType === "CombineAndPositionDrills") {
      const eligibleAmount =
        effectiveCombinePrice + effectivePositionDrillPrice;
      if (eligibleAmount <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("CombineAndPositionDrills"),
        };
      }
      return { ok: true, eligibleAmount };
    }

    if (couponType === "Subscription") {
      if (!selectedOptions.subscription || annualPrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Subscription"),
        };
      }
      return { ok: true, eligibleAmount: annualPrice };
    }

    return {
      ok: false,
      eligibleAmount: 0,
      message: couponNotApplicableMessage(couponType),
    };
  };

  const applyCouponCode = async () => {
    try {
      const response = await getCouponPrice(apiInstance, couponCode);

      const amount = Number(response?.amount ?? 0);
      const couponType = String(
        response?.type || response?.couponType || "",
      ).trim();
      const discountType = String(response?.discountType || "").trim();

      if (!amount || amount <= 0) {
        setCouponError("The coupon code is invalid or does not apply.");
        setDiscountInfo({ ...DEFAULT_DISCOUNT });
        trackEvent("Registration3_5_CouponValidationFailed", {
          coupon_code: couponCode,
          reason: "invalid_amount",
          portal_type: "highschool",
        });
        return;
      }

      if (!couponType) {
        setCouponError("Coupon type is missing. Please contact support.");
        setDiscountInfo({ ...DEFAULT_DISCOUNT });
        trackEvent("Registration3_5_CouponValidationFailed", {
          coupon_code: couponCode,
          reason: "missing_type",
          portal_type: "highschool",
        });
        return;
      }

      const { ok, eligibleAmount, message } =
        validateCouponApplicability(couponType);

      if (!ok) {
        setCouponError(
          message || "This coupon is not applicable to your current selection.",
        );
        setDiscountInfo({ ...DEFAULT_DISCOUNT });
        trackEvent("Registration3_5_CouponValidationFailed", {
          coupon_code: couponCode,
          coupon_type: couponType,
          reason: "not_applicable",
          portal_type: "highschool",
        });
        return;
      }

      let appliedBaseAmount = eligibleAmount;
      let appliedDiscountAmount = 0;
      let appliedPercentage = 0;

      if (couponType === "Combine" && discountType === "Percent Off") {
        appliedBaseAmount = Number(combinePrice || 0);
        appliedPercentage = amount;
        appliedDiscountAmount = Math.min(
          (appliedBaseAmount * amount) / 100,
          appliedBaseAmount,
        );
      } else if (couponType === "Combine" && discountType === "Amount Off") {
        appliedBaseAmount = Number(combinePrice || 0);
        appliedDiscountAmount = Math.min(amount, appliedBaseAmount);
      } else if (
        couponType === "CombineAndPositionDrills" &&
        discountType === "Percent Off"
      ) {
        appliedBaseAmount = Number(eligibleAmount || 0);
        appliedPercentage = amount;
        appliedDiscountAmount = Math.min(
          (appliedBaseAmount * amount) / 100,
          appliedBaseAmount,
        );
      } else if (discountType === "Percent Off") {
        appliedBaseAmount = Number(eligibleAmount || 0);
        appliedPercentage = amount;
        appliedDiscountAmount = Math.min(
          (appliedBaseAmount * amount) / 100,
          appliedBaseAmount,
        );
      } else {
        appliedBaseAmount = Number(eligibleAmount || 0);
        appliedDiscountAmount = Math.min(amount, appliedBaseAmount);
      }

      if (appliedDiscountAmount <= 0) {
        setCouponError("This coupon does not apply to the selected item(s).");
        setDiscountInfo({ ...DEFAULT_DISCOUNT });
        return;
      }

      const newDiscountInfo = {
        amount: appliedDiscountAmount,
        baseAmount: appliedBaseAmount,
        percentage: appliedPercentage,
        discountType,
        couponType,
      };

      setDiscountInfo(newDiscountInfo);
      setCouponError("");

      trackEvent("Registration3_5_AppliedCoupon", {
        event_id: user.eventId,
        coupon_code: couponCode,
        coupon_type: couponType,
        discount_amount: appliedDiscountAmount,
        portal_type: "highschool",
      });
    } catch (error) {
      console.error("Invalid coupon code", error);
      setCouponError("Invalid coupon code");
      setDiscountInfo({ ...DEFAULT_DISCOUNT });
    }
  };

  // ── Plan selection ─────────────────────────────────────────────────────────

  const handleSelectOption = (option) => {
    if (option === "positionDrill" && !hasPositionDrills) return;

    const updated = { ...selectedOptions, [option]: !selectedOptions[option] };
    updated.combine = true;

    setSelectedOptions(updated);

    const selectedPrice = computeSelectedPrice(updated);

    trackEvent("Highschool_MembershipOptionToggled", {
      event_id: user.eventId,
      option,
      enabled: updated[option],
      total_price: selectedPrice,
      portal_type: "highschool",
    });

    dispatch(
      setSubscriptionDetails({
        subscriptionType: option,
        subscriptionPrice: selectedPrice,
      }),
    );

    localStorage.setItem("subscriptionDetails", JSON.stringify(updated));
  };

  const handleContinueToPayment = async () => {
    const selectedPrice = computeSelectedPrice(selectedOptions);

    trackEvent("Highschool_ProceedToPay", {
      event_id: user.eventId,
      total_price: selectedPrice,
      include_position_drill: selectedOptions.positionDrill,
      include_subscription: selectedOptions.subscription,
      portal_type: "highschool",
    });

    // Persist validated coupon data for Payment.jsx to consume
    if (discountInfo.amount > 0) {
      localStorage.setItem("appliedCouponData", JSON.stringify(discountInfo));
      localStorage.setItem("appliedCouponCode", couponCode);
      localStorage.setItem("appliedCouponType", discountInfo.couponType);
    } else {
      localStorage.removeItem("appliedCouponData");
      localStorage.removeItem("appliedCouponCode");
      localStorage.removeItem("appliedCouponType");
    }

    if (selectedPrice <= 0) {
      // free path
      const userAthlete = await registerAthleteWithEventId(
        apiInstance,
        user,
        user.eventId,
      );
      try {
        const paymentPayloads = [];

        if (hasPositionDrills && selectedOptions.positionDrill) {
          paymentPayloads.push({
            userId: userAthlete.data.id,
            amount: 0,
            isCombine: false,
            isPositionDrill: true,
            isAnnualSubscription: false,
            isMonthlySubscription: false,
            isCash: false,
            eventId: user.eventId,
            transactionId: "N/A",
            intellipayToken: "",
          });
        }

        if (selectedOptions.combine) {
          paymentPayloads.push({
            userId: userAthlete.data.id,
            amount: 0,
            isCombine: true,
            isPositionDrill: false,
            isAnnualSubscription: false,
            isMonthlySubscription: false,
            isCash: false,
            eventId: user.eventId,
            transactionId: "N/A",
            intellipayToken: "",
          });
        }

        let allSuccessful = true;
        for (const payload of paymentPayloads) {
          const paid = await PostPayment(apiInstance, {
            ...payload,
            eventId: user.eventId,
          });
          if (paid.status !== 200) {
            allSuccessful = false;
            trackEvent("Registration4_5_PaymentFailed", {
              event_id: user.eventId,
              reason: "free_registration_payment_failed",
              portal_type: "highschool",
            });
            toast.error("Payment after spot registration failed.", {
              position: "top-right",
            });
            break;
          }
        }

        if (allSuccessful) {
          trackEvent("Highschool_FreeRegistrationCompleted", {
            event_id: user.eventId,
            include_position_drill: selectedOptions.positionDrill,
            portal_type: "highschool",
          });
          toast.success("Athlete spot registration & payment successful!", {
            position: "top-right",
          });
          setTimeout(() => navigate("/register/registrationsuccess"), 3000);
        }
      } catch (err) {
        console.error("Error during free registration:", err);
      }
    } else {
      window.location.href = "/register/subscription";
    }
  };

  const basePrice = computeSelectedPrice(selectedOptions);
  const discountedTotal = Math.max(
    basePrice - Number(discountInfo.amount || 0),
    0,
  );

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-[#363636] text-white w-full max-w-3xl mx-auto pt-16">
        <h2 className="text-center mb-2 text-white uppercase text-3xl font-black italic">
          Combine Registration
        </h2>
        <p className="text-gray-300 text-center my-0">
          Choose the perfect plan to level up your performance
        </p>

        <div className="text-red-500 text-center px-4 text-xl my-4 mb-6">
          {eventNameSelected}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20 ">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-4xl text-red-500"
            />
          </div>
        ) : (
          <>
            {/* Always-on Combine */}
            <div className="bg-[#1B1B1B] p-4 rounded-lg flex justify-between items-center mb-4 space-x-6 mx-4">
              <div className="flex items-center space-x-4 w-full">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="h-5 w-5 text-red-500 border-gray-700 rounded"
                />
                <img className="h-10" alt="Trackit" src="/combine.svg" />
                <div className="w-full">
                  <div className="flex justify-between items-center border-b border-gray-600 pb-2">
                    <span className="text-2xl font-semibold text-left">
                      Single Combine Pass
                    </span>
                    <span className="text-2xl font-semibold float-right">
                      ${combinePrice.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-400">
                      <span className="font-semibold text-gray-200 pr-2">
                        What's included:
                      </span>
                      • Complete Combine following NFL scouting combine protocol
                      <br />• Access to your verified combine numbers
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {hasPositionDrills && (
              <div className="bg-[#1B1B1B] p-4 rounded-lg flex justify-between items-center mb-4 space-x-6 mx-4">
                <div className="flex items-center space-x-4 w-full">
                  <input
                    type="checkbox"
                    checked={selectedOptions.positionDrill}
                    onChange={() => handleSelectOption("positionDrill")}
                    className="h-5 w-5 text-red-500 border-gray-700 rounded cursor-pointer"
                  />
                  <img className="h-10" alt="Trackit" src="/combine.svg" />
                  <div className="w-full">
                    <div className="flex justify-between items-center border-b border-gray-600 pb-2">
                      <span className="text-2xl font-semibold text-left">
                        Position Drills at this combine
                      </span>
                      <span className="text-2xl font-semibold float-right">
                        ${positionDrillPrice.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">&nbsp;</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Coupon code */}
            <div className="mx-4 mt-4 text-white">
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="coupon"
                  className="text-sm font-semibold whitespace-nowrap"
                >
                  Coupon Code:
                </label>
                <input
                  type="text"
                  id="coupon"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value);
                    setCouponError("");
                  }}
                  className="text-black px-2 py-1 rounded flex-1"
                  placeholder="Enter coupon code"
                />
                <button
                  onClick={applyCouponCode}
                  className="bg-white text-black px-3 py-1 rounded hover:bg-gray-200 whitespace-nowrap"
                >
                  Apply
                </button>
              </div>
              {couponError && (
                <p className="text-red-400 mt-2 text-sm">{couponError}</p>
              )}
              {!couponError && discountInfo.amount > 0 && (
                <p className="text-green-400 mt-2 text-sm">
                  Coupon applied — saving ${discountInfo.amount.toFixed(2)}
                </p>
              )}
            </div>

            {/* Footer summary */}
            <div className="mt-4 rounded-lg flex justify-between items-center max-w-3xl mx-auto">
              <div>
                <span className="text-sm font-normal text-red-500">
                  You selected:
                </span>
                <div className="flex flex-wrap gap-3 font-semibold">
                  <span>Single Combine Pass - ${combinePrice.toFixed(2)}</span>
                  {hasPositionDrills && selectedOptions.positionDrill && (
                    <span>
                      | Position Drills - ${positionDrillPrice.toFixed(2)}
                    </span>
                  )}
                </div>
                {discountInfo.amount > 0 && (
                  <div className="text-green-400 text-sm mt-1 font-semibold">
                    {discountInfo.discountType === "Percent Off"
                      ? `Discount (${discountInfo.percentage}%)`
                      : "Coupon"}{" "}
                    : - ${discountInfo.amount.toFixed(2)} → Total: $
                    {discountedTotal.toFixed(2)}
                  </div>
                )}
              </div>
              <div className="mt-6 text-center">
                <button
                  className="bg-red-500 text-white uppercase rounded px-6 py-2 text-center hover:bg-red-700 cursor-pointer whitespace-nowrap"
                  onClick={handleContinueToPayment}
                >
                  PROCEED TO PAY
                </button>
              </div>
            </div>
          </>
        )}
        <ToastContainer />
      </div>
    </>
  );
}
